import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { createTheme } from "@mui/material"

const defaultTheme = createTheme()

export const finalTheme = createTheme({
  palette: {
    primary: {
      main: "#2e4eaa",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#FD7E14",
      contrastText: "#ffffff",
    },
    success: {
      main: "#00a851",
    },
    warning: {
      main: "#e5b700",
    },
    info: {
      main: "#3b95d5",
    },
    error: {
      main: "#c30436",
    },
    fade: {
      main: "#0D1B44A7",
    },
    lightBlu: {
      main: "#F5F6FB",
    },
  },
  typography: {
    fontFamily: '"Open Sans", "Helvetica", "Arial", sans-serif',
    body1: {
      fontSize: "18px",
    },
    h2: {
      fontSize: "25px",
      fontWeight: "bold",
      textAlign: "center",
      textTransform: "uppercase",
    },
    h3: {
      textAlign: "center",
      fontSize: "20px",
    },
  },
  // @ts-ignore
  shadows: [
    ...defaultTheme.shadows.map((s) => {
      return s.replace("rgba(0,0,0,0.12)", "rgba(46, 78, 170,0.12)")
    }),
  ],
  components: {
    MuiGrid: {
      defaultProps: {
        alignItems: "center",
        justifyContent: "center",
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          borderRadius: "10px",
          boxShadow: theme.shadows[ownerState.elevation ?? 0],
        }),
      },
      variants: [
        {
          props: {
            variant: "dialog",
          },
          style: ({ theme }) => ({
            borderBottomLeftRadius: "0",
            borderTopLeftRadius: "0",
            position: "absolute",
            bottom: "0",
            left: "0",
            width: "85%",
            padding: "2em",
            marginBottom: "75px",
            transition: "height 1s linear",
          }),
        },
        {
          props: {
            variant: "fade",
          },
          style: ({ theme }) => ({
            position: "absolute",
            height: "100vh",
            width: "100vw",
            borderRadius: 0,
            backgroundColor: theme.palette.fade.main,
            pointerEvents: "all",
            zIndex: -1,
          }),
        },
        {
          props: {
            variant: "draggable",
          },
          style: () => ({
            position: "relative",
            borderRadius: "10px",
            padding: "0.1em 4em",
            height: "3em",
            minWidth: "10em",
            cursor: "move",
            margin: "0.4em",
            left: "-0.4em",
          }),
        },
        {
          props: {
            variant: "droppable",
          },
          style: ({ theme }) => ({
            height: "3.3em",
            minWidth: "18em",
            maxWidth: "18em",
            boxShadow: "inset 0 3px 6px #00000020",
            backgroundColor: theme.palette.grey[50],
            borderRadius: "10px",
            margin: "0.8em",
            zIndex: "0",
            overflow: "visible",
          }),
        },
        {
          props: {
            variant: "gameZone",
          },
          style: ({ theme }) => ({
            position: "absolute",
            width: "80%",
            height: "100vh",
            marginLeft: "20%",
            borderRadius: "0",
            padding: "1em",
            overflowY: "scroll",
          }),
        },
      ],
    },
    MuiButton: {
      defaultProps: {
        variant: "rexel",
      },
      variants: [
        {
          props: {
            variant: "rexel",
          },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            textTransform: "none",
            borderRadius: "5px",
            minWidth: "155px",
            width: "100%",
            minHeight: "62px",
            fontSize: "25px",
            ":hover": {
              backgroundColor: theme.palette.secondary.light,
            },
          }),
        },
      ],
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          userSelect: "none",
        },
      },
      variants: [
        {
          props: {
            variant: "dialog",
          },
          style: ({ theme }) => ({
            fontFamily: theme.typography.fontFamily,
            fontSize: "1.4em",
            lineHeight: "1.55",
            width: "auto",
          }),
        },
        {
          props: {
            variant: "poi",
          },
          style: ({ theme }) => ({
            fontFamily: theme.typography.fontFamily,
            fontSize: "1.4em",
            textAlign: "center",
            lineHeight: "1.8",
          }),
        },
      ],
    },
    MuiBackdrop: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.fade.main,
        }),
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: ({ theme }) => ({
          height: "20px",
          borderRadius: "10px",
          backgroundColor: theme.palette.grey[100],
        }),
      },
    },
    MuiCircularProgress: {},
    MuiCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.primary.main,
        }),
        title: ({ theme }) => ({
          color: theme.palette.primary.contrastText,
          textAlign: "center",
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {

        },

      }
    },
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          warning: <ErrorOutlineIcon fontSize="inherit" color='secondary' />
        }
      },
      styleOverrides: {
        standardWarning: ({ theme }) =>
        ({
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.common.white,
          borderRadius: "10px",
          boxShadow: theme.shadows[3],
        }),
      }
    }
  },
})

declare module "@mui/material/styles" {
  interface Palette {
    fade: Palette["primary"]
    lightBlu: Palette["primary"]
  }

  interface PaletteOptions {
    fade?: PaletteOptions["primary"]
    lightBlu?: PaletteOptions["primary"]
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    rexel: true
  }
}

declare module "@mui/material/Paper" {
  interface PaperPropsVariantOverrides {
    fade: true
    dialog: true
    gameZone: true
    draggable: true
    droppable: true
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    dialog: true
    poi: true
  }
}
